<script lang="ts" setup>
import { Popup, showLoadingToast } from "vant";
import { isMobile } from "@/modules/common";
import { ref, watch } from "vue";
import { useMatePersona } from "@/stores/matePersona";
import { getInterlocutorBySession, useChatSession } from "@/stores/chatSession";
import { MateSession } from "@/types/entity";

const props = defineProps<{
  show: boolean;
  session: MateSession;
}>();
const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
}>();

// States
const personaStore = useMatePersona();
const loading = ref(false);
const selectedPersonaId = ref<number | null>(null);
const sessionStore = useChatSession();
// Lifecycle
watch(
  () => props.show,
  async (show) => {
    if (show) {
      selectedPersonaId.value = null;
      loading.value = true;
      try {
        await personaStore.getOrFetchMatePersonaList();
        selectedPersonaId.value = props.session.matePersonaId ?? null;
      } finally {
        loading.value = false;
      }
    }
  }
);

// Event Handler
const onConfirmEdit = async () => {
  const loading = showLoadingToast();
  try {
    await sessionStore.updateSession(props.session.id, {
      matePersonaId: selectedPersonaId.value,
    });
    emit("update:show", false);
  } finally {
    loading.close();
  }
};
</script>
<template>
  <Popup
    :class="{
      'w-full': isMobile(),
      'w-[40rem]': !isMobile(),
      'max-h-[90%]': isMobile(),
      'max-h-[35rem]': !isMobile(),
    }"
    :position="isMobile() ? 'bottom' : 'center'"
    :show="show"
    class="grid grid-rows-[auto_1fr] gap-2 overflow-hidden"
    round
    teleport="body"
    @update:show="emit('update:show', $event)"
  >
    <div class="flex justify-between items-center p-4 pb-0">
      <button
        class="btn btn-xs rounded btn-ghost"
        @click="emit('update:show', false)"
      >
        {{ $t("Buttons.cancel") }}
      </button>
      <button class="btn btn-xs rounded btn-primary" @click="onConfirmEdit">
        {{ $t("Buttons.confirm") }}
      </button>
    </div>
    <div
      class="flex-grow h-full flex gap-4 flex-col sm:flex-row overflow-y-auto p-4"
    >
      <div
        v-if="loading || personaStore.genericMatePersonaList.length > 0"
        class="grid grid-cols-1 sm:grid-cols-2 gap-2 flex-grow"
      >
        <template v-if="loading">
          <div class="flex justify-between rounded-lg border overflow-hidden">
            <div
              class="skeleton-loading-bg aspect-[3/4] overflow-hidden w-[30%]"
            />
            <div class="flex flex-col justify-between p-2 flex-grow">
              <div class="flex flex-col gap-2 flex-grow">
                <div class="w-[100%] skeleton-loading-bg h-5 rounded" />
                <div class="w-[80%] skeleton-loading-bg h-5 rounded" />
                <div class="w-[40%] skeleton-loading-bg h-5 rounded" />
                <div class="w-[70%] skeleton-loading-bg h-5 rounded" />
              </div>
              <div class="flex justify-end">
                <div
                  class="w-5 aspect-square skeleton-loading-bg h-5 rounded"
                />
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div
            :class="{
              '': selectedPersonaId,
              'border-primary text-primary': !selectedPersonaId,
            }"
            class="flex gap-2 border p-2 h-32 rounded cursor-pointer items-center justify-center"
            @click="selectedPersonaId = null"
          >
            {{ $t("Chats.matePersona.default") }}
          </div>
          <PersonaCard
            v-for="item in personaStore.genericMatePersonaList"
            :key="item.id"
            :mate-persona="item"
            :selected="selectedPersonaId === item.id"
            :target-mate="getInterlocutorBySession(session)!"
            @click="selectedPersonaId = item.id"
          />
        </template>
      </div>
      <div v-else class="w-full">
        <EmptyContent :title="$t('Chats.matePersona.emptyTitle')" />
      </div>
    </div>
  </Popup>
</template>
